/**
 * Model/Playbook Routes
 *
 * Routes for playbook building, including:
 * - Dashboard and details
 * - Rule editing wizards (point edit, FXI, leaf list, etc.)
 * - Answer questions flow
 * - Question builder
 */
import React from 'react';
import { createRoute } from '@tanstack/react-router';
import { createPermissionBeforeLoad } from '@bb/session-manager';
import { authLayoutRoute } from '../rootRoute';
import * as groups from '../../groups.config';

// ============================================
// PERMISSION CHECK (reused across all routes)
// ============================================
const playbookPermission = createPermissionBeforeLoad({
  permissions: [groups.PLAYBOOK_BUILDER_PERMISSION],
  forbiddenPath: '/bbweb/home',
});

// ============================================
// LAZY PAGE LOADERS
// ============================================
const PlaybookDashboard = React.lazy(() => import('../../pages/PlaybookDashboard/PlaybookDashboard'));
const PlaybookDetails = React.lazy(() => import('../../pages/PlaybookDetails/PlaybookDetails'));
const PlaybookDataGrid = React.lazy(() => import('../../pages/PlaybookDataGrid/PlaybookDataGrid'));
const RuleDetails = React.lazy(() => import('../../pages/RuleDetails/RuleDetails'));
const CustomBuild = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/CustomBuild/CustomBuild'));
const ImportRules = React.lazy(() => import('../../pages/PlaybookBuilder/ImportRules/ImportRules'));

// Point Edit
const PointEdit1 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/point-edit/step1'));
const PointEdit2 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/point-edit/step2'));
const PointEdit3 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/point-edit/step3'));

// Sub Edit
const SubEdit1 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/sub-edit/step1'));
const SubEdit2 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/sub-edit/step2'));
const SubEdit3 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/sub-edit/step3'));

// FXI (paragraph insert)
const Fxi1 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/paragraph-insert/step1'));
const Fxi2 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/paragraph-insert/step2'));
const Fxi3 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/paragraph-insert/step3'));
const Fxi3Tail = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/paragraph-insert/step3-tail'));
const Fxi4 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/paragraph-insert/step4'));
const Fxi5 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/paragraph-insert/step5'));
const Fxi5PresenceThreshold = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/paragraph-insert/step5-presence-threshold'));
const Fxi5ContextThreshold = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/paragraph-insert/step5-context-threshold'));
const Fxi5TextRange = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/paragraph-insert/step5-text-range'));

// Leaf List Edit
const LeafList1 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/leaf-list-edit/step1'));
const LeafList2 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/leaf-list-edit/step2'));
const LeafList3 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/leaf-list-edit/step3'));

// Governing Law
const GoverningLaw1 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/governing-law/step1'));
const GoverningLaw2 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/governing-law/step2'));
const GoverningLaw3 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/governing-law/step3'));

// Forum
const Forum1 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/forum/step1'));
const Forum2 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/forum/step2'));
const Forum3 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/forum/step3'));

// Term
const Term1 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/term/step1'));
const Term2 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/term/step2'));
const Term3 = React.lazy(() => import('../../pages/PlaybookBuilder/custom-build/term/step3'));

// Answer Questions
const AnswerQuestionsSectionSelection = React.lazy(() => import('../../pages/PlaybookBuilder/answer-questions/SectionSelection/SectionSelection'));
const AnswerQuestionsStatus = React.lazy(() => import('../../pages/PlaybookBuilder/answer-questions/Status/Status'));
const AnswerQuestionsStepController = React.lazy(() => import('../../pages/PlaybookBuilder/answer-questions/StepController/StepController'));

// Question Builder
const QuestionBuilder = React.lazy(() => import('../../pages/PlaybookBuilder/question-builder/QuestionBuilder/QuestionBuilder'));

// ============================================
// MAIN MODEL ROUTES
// ============================================

/**
 * Playbook Dashboard - list of all playbooks
 */
export const modelDashboardRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model',
  beforeLoad: playbookPermission,
  component: PlaybookDashboard,
});

/**
 * Playbook Details - single playbook view
 */
export const modelDetailsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id',
  beforeLoad: playbookPermission,
  component: PlaybookDetails,
});

/**
 * Playbook Data Grid
 */
export const modelDataGridRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/data-grid',
  beforeLoad: playbookPermission,
  component: PlaybookDataGrid,
});

/**
 * Rule Details
 */
export const ruleDetailsRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rule/$ruleId',
  beforeLoad: playbookPermission,
  component: RuleDetails,
});

/**
 * Custom Build landing
 */
export const customBuildRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/custom-build',
  beforeLoad: playbookPermission,
  component: CustomBuild,
});

/**
 * Import Rules
 */
export const importRulesRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/import-rules/1',
  beforeLoad: playbookPermission,
  component: ImportRules,
});

// ============================================
// POINT EDIT WIZARD (steps 1-3)
// ============================================
export const pointEdit1Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/pointedit/1',
  beforeLoad: playbookPermission,
  component: PointEdit1,
});

export const pointEdit2Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/pointedit/2',
  beforeLoad: playbookPermission,
  component: PointEdit2,
});

export const pointEdit3Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/pointedit/3',
  beforeLoad: playbookPermission,
  component: PointEdit3,
});

// ============================================
// SUB EDIT WIZARD (steps 1-3)
// ============================================
export const subEdit1Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/sub-edit/1',
  beforeLoad: playbookPermission,
  component: SubEdit1,
});

export const subEdit2Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/sub-edit/2',
  beforeLoad: playbookPermission,
  component: SubEdit2,
});

export const subEdit3Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/sub-edit/3',
  beforeLoad: playbookPermission,
  component: SubEdit3,
});

// ============================================
// FXI WIZARD (paragraph insert)
// ============================================
export const fxi1Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/fxi/1',
  beforeLoad: playbookPermission,
  component: Fxi1,
});

export const fxi2Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/fxi/2',
  beforeLoad: playbookPermission,
  component: Fxi2,
});

export const fxi3Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/fxi/3',
  beforeLoad: playbookPermission,
  component: Fxi3,
});

export const fxi3TailRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/fxi/3/tail',
  beforeLoad: playbookPermission,
  component: Fxi3Tail,
});

export const fxi4Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/fxi/4',
  beforeLoad: playbookPermission,
  component: Fxi4,
});

export const fxi5Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/fxi/5',
  beforeLoad: playbookPermission,
  component: Fxi5,
});

export const fxi5PresenceRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/fxi/presence-threshold',
  beforeLoad: playbookPermission,
  component: Fxi5PresenceThreshold,
});

export const fxi5SampleRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/fxi/5/sample',
  beforeLoad: playbookPermission,
  component: Fxi5ContextThreshold,
});

export const fxi5TextRangeRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/fxi/5/text-range',
  beforeLoad: playbookPermission,
  component: Fxi5TextRange,
});

// ============================================
// LEAF LIST EDIT WIZARD
// ============================================
export const leafList1Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/leaf-list/1',
  beforeLoad: playbookPermission,
  component: LeafList1,
});

export const leafList2Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/leaf-list/2',
  beforeLoad: playbookPermission,
  component: LeafList2,
});

export const leafList3Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/leaf-list/3',
  beforeLoad: playbookPermission,
  component: LeafList3,
});

// ============================================
// GOVERNING LAW WIZARD
// ============================================
export const governingLaw1Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/governing-law/1',
  beforeLoad: playbookPermission,
  component: GoverningLaw1,
});

export const governingLaw2Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/governing-law/2',
  beforeLoad: playbookPermission,
  component: GoverningLaw2,
});

export const governingLaw3Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/governing-law/3',
  beforeLoad: playbookPermission,
  component: GoverningLaw3,
});

// ============================================
// FORUM WIZARD
// ============================================
export const forum1Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/forum/1',
  beforeLoad: playbookPermission,
  component: Forum1,
});

export const forum2Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/forum/2',
  beforeLoad: playbookPermission,
  component: Forum2,
});

export const forum3Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/forum/3',
  beforeLoad: playbookPermission,
  component: Forum3,
});

// ============================================
// TERM WIZARD
// ============================================
export const term1Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/term/1',
  beforeLoad: playbookPermission,
  component: Term1,
});

export const term2Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/term/2',
  beforeLoad: playbookPermission,
  component: Term2,
});

export const term3Route = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/term/3',
  beforeLoad: playbookPermission,
  component: Term3,
});

// ============================================
// ANSWER QUESTIONS
// ============================================
export const aqSelectRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/aq/select',
  beforeLoad: playbookPermission,
  component: AnswerQuestionsSectionSelection,
});

export const aqStatusRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/aq/status',
  beforeLoad: playbookPermission,
  component: AnswerQuestionsStatus,
});

/**
 * Dynamic answer questions route with section parameter
 * This replaces the dynamic route generation from Redux state
 */
export const aqSectionRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/aq/$sectionId',
  beforeLoad: playbookPermission,
  component: AnswerQuestionsStepController,
});

// ============================================
// QUESTION BUILDER
// ============================================
export const questionBuilderRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/model/edit/$id/rules/qb/base',
  beforeLoad: playbookPermission,
  component: QuestionBuilder,
});

// ============================================
// EXPORT ALL MODEL ROUTES
// ============================================
export const modelRoutes = [
  modelDashboardRoute,
  modelDetailsRoute,
  modelDataGridRoute,
  ruleDetailsRoute,
  customBuildRoute,
  importRulesRoute,
  // Point Edit
  pointEdit1Route,
  pointEdit2Route,
  pointEdit3Route,
  // Sub Edit
  subEdit1Route,
  subEdit2Route,
  subEdit3Route,
  // FXI
  fxi1Route,
  fxi2Route,
  fxi3Route,
  fxi3TailRoute,
  fxi4Route,
  fxi5Route,
  fxi5PresenceRoute,
  fxi5SampleRoute,
  fxi5TextRangeRoute,
  // Leaf List
  leafList1Route,
  leafList2Route,
  leafList3Route,
  // Governing Law
  governingLaw1Route,
  governingLaw2Route,
  governingLaw3Route,
  // Forum
  forum1Route,
  forum2Route,
  forum3Route,
  // Term
  term1Route,
  term2Route,
  term3Route,
  // Answer Questions
  aqSelectRoute,
  aqStatusRoute,
  aqSectionRoute,
  // Question Builder
  questionBuilderRoute,
];
